import {
  NotificationsContext,
  UpgradeModalContext,
  UserContext,
  VibratoClientContext
} from "@/components/dashboard/contexts"
import usertour from "usertour.js"
import { BlankLoading } from "@/components/Loading"
import CallService from "@/lib/calls"
import { formatAnyPhoneNumber } from "@/lib/phone"
import { DynamicTaskService, RecipientService } from "@/lib/tasks"
import TranscriptService from "@/lib/transcript"
import { ApiError, Call, CallbackPhoneNumber } from "@/lib/vibrato-api"
import { Disclosure, DisclosureButton, DisclosurePanel } from "@headlessui/react"
import { Modal, ModalBody, ModalFooter, ModalHeader } from "flowbite-react"
import {
  CheckCircle,
  HeadsetHelp,
  NavArrowDown,
  Pause,
  Phone,
  PhoneXmark,
  Play,
  ShareAndroid,
  Shield,
  AlignLeft,
  Clock,
  VoiceCircle
} from "iconoir-react"
import moment from "moment"
import { useCallback, useContext, useEffect, useRef, useState } from "react"
import { Link, useLocation, useParams, useSearchParams } from "react-router-dom"
import CallResults from "./CallResults"
import CallSettingsCard from "./CallSettingCard"
import LiveCallPlayback from "./LiveCallPlayback"
import RateCallModal from "./RateCallModal"
import RetryInfoCard from "./RetryInfoCard"
import Transcript from "./Transcript"
import useRefreshCallOnResume from "@/hooks/useRefreshCallOnResume"
import React from "react"
import TourTrigger from "@/components/TourTrigger"
import NewerCallAvailableModal from "./NewerCallAvailableModal"

// Helper function to determine if the tour should be shown
function shouldShowTour(userSession: any, forceShow: boolean = false): boolean {
  if (!userSession) return false

  // If forced via URL param or manual trigger, always show
  if (forceShow) return true

  // Check if tour has been shown before in localStorage
  const tourShownKey = `tour_shown_${userSession.id}`
  const tourShown = localStorage.getItem(tourShownKey)
  if (tourShown === "true") {
    return false
  }

  // Check if user was created more than a week ago
  if (userSession.created_at) {
    const userCreatedAt = new Date(userSession.created_at)
    const oneWeekAgo = new Date()
    oneWeekAgo.setDate(oneWeekAgo.getDate() - 7)

    if (userCreatedAt < oneWeekAgo) {
      return false
    }
  }

  return true
}

// Helper function to mark tour as shown
function markTourAsShown(userSession: any): void {
  if (userSession) {
    const tourShownKey = `tour_shown_${userSession.id}`
    localStorage.setItem(tourShownKey, "true")
  }
}

export default function ViewCall(): React.JSX.Element {
  const { callId } = useParams()
  const location = useLocation()
  const [searchParams] = useSearchParams()

  const vibratoClient = useContext(VibratoClientContext)
  const upgradeModal = useContext(UpgradeModalContext)
  const userSession = useContext(UserContext)

  const [taskRecipientSvc, setTaskRecipientSvc] = useState<RecipientService | null>(null)
  const [dynTaskSvc, setDynTaskSvc] = useState<any | null>(null)
  const [call, setCall] = useState<any>(null)
  const [callService, setCallService] = useState<any>(null)
  const [messages, setMessages] = useState<any[]>([])
  const transcriptSvcRef = useRef<TranscriptService>(new TranscriptService())
  const [callResults, setCallResults] = useState(null)
  const [recordingUrl, setRecordingUrl] = useState(null)
  const [userDocuments, setUserDocuments] = useState<any[]>([])
  const [playCall, setPlayCall] = useState(false)
  const [isPlaying, setIsPlaying] = useState(false)
  const [showTakeOverModal, setShowTakeOverModal] = useState(false)
  const [scheduledAt, setScheduledAt] = useState<string | null>(null)
  const notifications = useContext(NotificationsContext)
  const [isCancellingRetries, setIsCancellingRetries] = useState(false)
  const [showShareModal, setShowShareModal] = useState(false)
  const [showTranscriptModal, setShowTranscriptModal] = useState(false)
  const [plan, setPlan] = useState<any | null>(null)
  const [showFeedbackModal, setShowFeedbackModal] = useState(false)
  const [showNewerCallModal, setShowNewerCallModal] = useState(false)
  const [newerCallId, setNewerCallId] = useState<number | null>(null)
  const [alreadyShownFeedbackModal, setAlreadyShownFeedbackModal] = useState(false)

  useEffect(() => {
    setAlreadyShownFeedbackModal(false)
    setShowNewerCallModal(false)
    setShowFeedbackModal(false)
    setNewerCallId(null)
  }, [callId])

  const updateCall = useCallback(
    (newCall: Call | null) => {
      if (!newCall) {
        console.log("updateCall: No call object provided, returning.")
        return
      }

      console.log(`updateCall: Processing call ID ${newCall.id} with status ${newCall.status}`)

      // If we've already shown a modal for this call view, don't re-evaluate.
      // This state is reset when the callId changes.
      // if (alreadyShownFeedbackModal) {
      //   console.log("updateCall: Modal already shown for this view, skipping logic.")
      //   setCall(newCall)
      //   return
      // }

      const { retry_schedule, status } = newCall

      // Case 1: No retry schedule. Show feedback modal once the call is complete.
      if (!retry_schedule) {
        console.log("updateCall: No retry schedule found.")
        if (status === "completed" || status === "errored") {
          console.log("updateCall: Call is complete, showing feedback modal.")
          setShowFeedbackModal(true)
          setAlreadyShownFeedbackModal(true)
        }
        setCall(newCall)
        return
      }

      console.log("updateCall: Retry schedule found.", retry_schedule)
      // Case 2: Retry schedule exists.
      const { call_retries, completed } = retry_schedule

      // If no retries attempted yet, and schedule is not complete, just wait.
      if (call_retries.length === 0 && !completed) {
        console.log("updateCall: Retries scheduled but none attempted yet. Waiting.")
        setCall(newCall)
        return
      }

      // If schedule completed with no actual retries (e.g., canceled).
      if (call_retries.length === 0 && completed) {
        console.log("updateCall: Retry schedule complete with no retries. Showing feedback modal.")
        setShowFeedbackModal(true)
        setAlreadyShownFeedbackModal(true)
        setCall(newCall)
        return
      }

      const lastRetry = call_retries[call_retries.length - 1]
      console.log(`updateCall: Last retry call ID is ${lastRetry.id}.`)

      // If viewing an older call in the retry chain, prompt to view the latest one.
      if (newCall.id !== lastRetry.id) {
        console.log("updateCall: Viewing an older call in the retry chain.")
        if (newerCallId !== lastRetry.id) {
          console.log("updateCall: New retry call available, showing 'NewerCallAvailableModal'.")
          setNewerCallId(lastRetry.id)
          setShowNewerCallModal(true)
          setAlreadyShownFeedbackModal(true) // Prevent other modals from appearing on this view
        }
        setCall(newCall)
        return
      }

      // At this point, we are viewing the latest call attempt.
      console.log("updateCall: Viewing the latest call in the retry chain.")
      // Show feedback modal only if the entire retry sequence is complete.
      if (completed) {
        console.log("updateCall: Retry sequence is complete. Showing feedback modal.")
        setShowFeedbackModal(true)
        setAlreadyShownFeedbackModal(true)
      } else {
        console.log("updateCall: Retry sequence is not yet complete. Waiting.")
      }

      setCall(newCall)
    },
    [callId, newerCallId, alreadyShownFeedbackModal]
  )

  // Function to manually trigger the tour
  const triggerTour = useCallback(() => {
    console.log("Manually triggering tour")
    usertour.start("cmcl5dtri02cua3ms7tm36nu4")
    markTourAsShown(userSession)
  }, [userSession])

  // useEffect(() => {
  //   setMessages([])
  //   setTranscriptSvc(new TranscriptService())
  // }, [location.pathname])

  useEffect(() => {
    async function loadCall(): Promise<void> {
      if (vibratoClient == null || callId == null) {
        return
      }

      // Reset states
      setCall(null)
      setCallService(null)
      setScheduledAt(null)
      setDynTaskSvc(null)
      setTaskRecipientSvc(null)
      setUserDocuments([])
      setCallResults(null)
      setRecordingUrl(null)
      // setAlreadyShownFeedbackModal(false)
      // setShowNewerCallModal(false)
      // setShowFeedbackModal(false)
      // setNewerCallId(null)
      // setMessages([])

      const call = await vibratoClient.getCall(callId)
      updateCall(call)

      setCallService(new CallService(call))
      setScheduledAt(call.scheduled_at)
      setDynTaskSvc(new DynamicTaskService(call.dynamic_task))
      setTaskRecipientSvc(new RecipientService(call.task_recipient))

      const documentUrls = await vibratoClient.getDocumentUrls(callId)
      setUserDocuments(documentUrls)

      displayCallResults(call)

      // Show tour conditionally based on localStorage, user age, and URL params
      setTimeout(() => {
        const forceTour = searchParams.get("tour") === "true"
        if (shouldShowTour(userSession, forceTour)) {
          console.log("Starting tour")
          usertour.start("cmcl5dtri02cua3ms7tm36nu4")
          markTourAsShown(userSession)
        } else {
          console.log("Tour skipped - already shown or user too old")
        }
      }, 2000)
    }

    void loadCall()
  }, [vibratoClient, callId, location.pathname, userSession, searchParams, updateCall])

  useEffect(() => {
    vibratoClient.getPlan().then(setPlan).catch(console.error)
  }, [vibratoClient])

  const addNewMessage = (msg: string, meta: any, transcriptSvc: TranscriptService) => {
    // if (transcriptSvc == null) {
    //   return
    // }

    let speaker = "human"
    if (meta.inner_msg_type == "AIMessage") {
      speaker = "ai"
    } else if (meta.inner_msg_type === "SystemMessage") {
      speaker = "you"
    } else if (meta.inner_msg_type === "HumanMessage") {
      speaker = "human"
    }

    let text = msg.trim()

    text = text.replace("<<h>>.", "")
    text = text.replace("<<a>>.", "")
    text = text.replace("<<hangup>>", "")
    text = text.replace("<<handoff>>", "")

    if (text == "<<nothing>>") {
      return
    }

    text = text.trim()
    if (text.startsWith("DTMF:")) {
      text = text.replace("DTMF:", "")
      text = text.trim()
    }

    text = text.replace(/<<pause>>/g, " ")
    text = text.replace(/<<speaker-\d+>>/g, "")
    text = text.trim()

    if (text == "") {
      return
    }

    const m = {
      id: meta.id,
      text: text,
      speaker: speaker,
      isFinal: meta.is_final
    }

    console.log("Adding new message to state: ", m)

    transcriptSvc.addMessage(m)
    setMessages(transcriptSvc.getReversedMessages())
  }

  useEffect(() => {
    if (call == null) {
      return
    }

    setMessages([])

    const tSvc = new TranscriptService()
    transcriptSvcRef.current = tSvc

    if (call.transcript != null) {
      for (const m of call.transcript.messages) {
        addNewMessage(m?.display_msg || m.inner_msg.content, m, tSvc)
      }
    }
  }, [call, callId, location.pathname])

  const displayCallResults = useCallback(
    async (newCall: any) => {
      if (!newCall) {
        newCall = await vibratoClient.getCall(callId)
      }
      if (newCall.call_transferred) {
        setPlayCall(false)
      }
      if (newCall.status != "completed" && newCall.status != "errored") {
        return
      }

      if (newCall.transcript != null) {
        for (const m of newCall.transcript.messages) {
          addNewMessage(m?.display_msg || m.inner_msg.content, m, transcriptSvcRef.current)
        }
      }

      // TODO: this might not be available right away
      const recordingUrl = await vibratoClient.getCallRecording(callId)

      updateCall(newCall)
      setCallResults(newCall)
      setRecordingUrl(recordingUrl)
    },
    [vibratoClient, callId, updateCall, setPlayCall]
  )

  useEffect(() => {
    if (call != null) {
      setCallService(new CallService(call))
    }
  }, [call])

  const handleHangupClick = useCallback(async () => {
    if (vibratoClient == null) {
      return
    }

    console.log("Hanging up...")

    // Disable end-button and store original state
    const endButton = document.getElementById("end-button")
    const originalText = endButton?.innerText || "END CALL"

    if (endButton != null) {
      endButton.setAttribute("disabled", "disabled")
      endButton.innerText = "ENDING..."
    }

    let lastError: any = null

    // Retry logic with exponential backoff (3 attempts)
    for (let attempt = 1; attempt <= 3; attempt++) {
      try {
        await vibratoClient.endCall(callId)
        // Success - proceed with normal flow
        void displayCallResults(null)
        return
      } catch (e) {
        lastError = e
        console.warn(`Error ending call (attempt ${attempt}/3):`, e)

        if (attempt < 3) {
          // Wait with exponential backoff: 1s, 2s, 4s
          const delay = Math.pow(2, attempt - 1) * 1000
          console.log(`Retrying in ${delay}ms...`)
          await new Promise((resolve) => setTimeout(resolve, delay))
        }
      }
    }

    // All retries failed - reset button state
    console.warn("Failed to end call after 3 attempts:", lastError)
    if (endButton != null) {
      endButton.removeAttribute("disabled")
      endButton.innerText = originalText
    }
  }, [vibratoClient, callId, displayCallResults])

  const handleCancelScheduledCall = useCallback(async () => {
    console.log("Canceling scheduled call...")

    if (vibratoClient != null) {
      try {
        await vibratoClient.endCall(callId)
        setScheduledAt(null)
        notifications({
          type: "success",
          header: "Success",
          id: "cancel-scheduled-call-success",
          message: "Scheduled call has been canceled."
        })
      } catch (e) {
        console.log("Error canceling scheduled call: ", e)
        notifications({
          type: "error",
          header: "Error",
          id: "cancel-scheduled-call-error",
          message: "Failed to cancel scheduled call. Please try again."
        })
      }
    }

    void displayCallResults(null)
  }, [vibratoClient, callId, displayCallResults, notifications])

  const handleCancelRetries = useCallback(async () => {
    console.log("Canceling retries...")
    setIsCancellingRetries(true)

    if (vibratoClient != null && call.retry_schedule) {
      try {
        const lastRetryCall = call.retry_schedule.call_retries[call.retry_schedule.call_retries.length - 1]
        await vibratoClient.endCall(lastRetryCall.id)

        let updatedCall = call
        while (!updatedCall.retry_schedule.completed) {
          await new Promise((resolve) => setTimeout(resolve, 2000)) // Wait for 2 seconds before checking again
          updatedCall = await vibratoClient.getCall(callId)
        }

        updateCall(updatedCall)

        notifications({
          type: "success",
          header: "Success",
          id: "cancel-retries-success",
          message: "Retries have been canceled."
        })
      } catch (e) {
        console.log("Error canceling retries: ", e)
        notifications({
          type: "error",
          header: "Error",
          id: "cancel-retries-error",
          message: "Failed to cancel retries. Please try again."
        })
      } finally {
        setIsCancellingRetries(false)
      }
    }
  }, [vibratoClient, call, callId, notifications, updateCall])

  const initClientWSApi = useCallback(() => {
    let ws: WebSocket | null = null
    let shouldReconnect = true
    let retryAttempts = 0
    const maxRetries = 5

    const connect = async () => {
      if (!shouldReconnect) return

      console.log("WS connecting...")
      ws = await vibratoClient.callWebsocket(callId)

      if (!ws) return

      ws.onopen = () => {
        console.log("WS connection opened")
        // Reset retry counter on successful connection
        retryAttempts = 0
      }

      ws.onerror = (error: any) => {
        console.log("WS error: ", error)
      }

      ws.onmessage = (event: any) => {
        try {
          const data = JSON.parse(event.data)

          if (data.event_type === "model_call_updated") {
            vibratoClient
              .getCall(callId)
              .then((c: any) => {
                const svc = new CallService(c)
                if (svc.completed()) {
                  console.log("Call completed, hanging up", data)
                  void handleHangupClick()
                  updateCall(c)
                  displayCallResults(c)
                } else {
                  updateCall(c)
                  setCallService(new CallService(c))
                }
              })
              .catch((error: unknown) => {
                console.error("Error fetching updated call:", error)
              })
            return
          }

          if (data.event_type === "model_call_conversation_msg") {
            const message = data.msg?.display_msg || data.msg.inner_msg.content
            addNewMessage(message, data.msg, transcriptSvcRef.current)
          }
        } catch (error: unknown) {
          console.error("Error parsing WebSocket message:", error)
        }
      }

      ws.onclose = (event) => {
        console.log("WS connection closed")

        // Reconnect with exponential backoff if not manually closed
        if (shouldReconnect && event.code !== 1000) {
          if (retryAttempts < maxRetries) {
            retryAttempts++
            // Exponential backoff: 1s, 2s, 4s, 8s, 16s
            const backoffDelay = Math.pow(2, retryAttempts - 1) * 1000
            console.log(`WS reconnecting in ${backoffDelay}ms (attempt ${retryAttempts}/${maxRetries})`)

            setTimeout(() => {
              if (shouldReconnect) {
                connect().catch(console.error)
              }
            }, backoffDelay)
          } else {
            console.log("WS max retries reached, giving up silently")
            shouldReconnect = false
          }
        }
      }
    }

    // Initial connection
    connect().catch(console.error)

    // Return WebSocket with cleanup
    return {
      close: () => {
        shouldReconnect = false
        if (ws) {
          ws.close(1000, "Manual close")
        }
      }
    }
  }, [vibratoClient, callId, handleHangupClick, displayCallResults, updateCall])

  useEffect(() => {
    const ws = initClientWSApi()
    return () => {
      console.log("Cleaning up WS connection...")
      ws.close()
    }
  }, [callId])

  useEffect(() => {
    if (call == null || call.status != "queued") {
      return
    }

    const handle = setInterval(async () => {
      console.log("Checking dequeue call status...")

      const c = await vibratoClient.getCall(callId)
      updateCall(c)
    }, 5000)

    return () => {
      clearInterval(handle)
    }
  }, [call, callId, vibratoClient, updateCall])

  const sendInstructions = useCallback(
    async (message: string) => {
      if (vibratoClient == null) {
        return
      }

      await vibratoClient.createCallInstructions(callId, message)
    },
    [vibratoClient]
  )

  const handleShareClick = () => {
    setShowShareModal(true)
  }

  const handleFeedbackModalClose = () => {
    setShowFeedbackModal(false)
    if (call?.status === "completed" && call?.succeeded && plan?.plan_slug === "freemium") {
      upgradeModal.setShowModal(true)
    }
  }

  useRefreshCallOnResume(vibratoClient, callId ? Number(callId) : null, (newCall: Call) => {
    updateCall(newCall)
    setCallService(new CallService(newCall))
    const callSvc = new CallService(newCall)
    if (callSvc.completed()) {
      setCallResults(newCall as any)
    }
  })

  if (call == null) {
    return <BlankLoading />
  }

  return (
    <div className="h-full w-full mx-auto p-4 md:p-6 lg:pt-8 lg:pb-0 lg:px-8 flex flex-col">
      {call != null && callService != null && taskRecipientSvc != null && (
        <>
          <NewerCallAvailableModal
            show={showNewerCallModal}
            setShow={setShowNewerCallModal}
            newerCallId={newerCallId}
            onStay={() => {
              if (call.status === "completed" || call.status === "errored") {
                setShowFeedbackModal(true)
              }
            }}
          />
          <LiveCallPlayback
            key={`playback-${call.id}`}
            callId={call.id}
            playCall={playCall}
            isPlaying={isPlaying}
            setIsPlaying={setIsPlaying}
          />
          <TakeOverCallModal
            key={`takeover-${call.id}`}
            call={call}
            showModal={showTakeOverModal}
            setShowModal={setShowTakeOverModal}
          />
          <ShareCallModal
            key={`share-${call.id}`}
            call={call}
            showModal={showShareModal}
            setShowModal={setShowShareModal}
          />
          {callService.completed() && call.summary && showFeedbackModal && (
            <RateCallModal
              key={`rate-${call.id}`}
              call={call}
              callService={callService}
              onClose={handleFeedbackModalClose}
            />
          )}

          {/* Header Section */}
          <div className="flex flex-row flex-wrap justify-between gap-4 mb-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-purple-100 rounded-xl">
                <Phone width="24" className="text-purple-600" />
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-gray-900">{callService.getFrontendNameShort()}</h1>
                <p className="text-gray-600">{callService.getFrontendDescription()}</p>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              {callService.completed() && call.summary && callResults && <CallStatusBadge call={call} />}

              {!callService.preparing() && <TotalTimeButton call={call} />}

              {callService.completed() && call.summary && callResults && (
                <button
                  onClick={handleShareClick}
                  className="bg-linear-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-medium px-4 py-2 rounded-xl shadow-sm hover:shadow-md transition-all flex items-center gap-2"
                >
                  <ShareAndroid className="w-4 h-4" />
                  Share
                </button>
              )}
            </div>
          </div>

          {/* Main Content Grid Layout */}
          <div className="flex-1 grid grid-cols-1 lg:grid-cols-12 gap-6 lg:overflow-hidden">
            {/* Left Column - Call Info - scrollable */}
            <div className="lg:col-span-5 overflow-y-auto pr-2">
              <div className="flex flex-col gap-2 pb-4">
                {/* Call Details Card */}
                <div className="bg-white shadow-sm rounded-xl border border-gray-200 overflow-hidden">
                  <Disclosure defaultOpen>
                    {({ open }) => (
                      <div>
                        <DisclosureButton
                          id="call-details-disclosure-button"
                          className="flex w-full justify-between items-center p-5 hover:bg-gray-50 transition-colors"
                        >
                          <div className="flex items-center gap-3">
                            <div className="p-2 bg-purple-100 rounded-lg">
                              <VoiceCircle className="w-5 h-5 text-purple-600" />
                            </div>
                            <h3 className="text-base font-medium text-gray-900">Call Details</h3>
                          </div>
                          <NavArrowDown
                            className={`${
                              open ? "rotate-180 transform" : ""
                            } h-5 w-5 text-blue-500 transition-transform duration-200`}
                          />
                        </DisclosureButton>
                        <DisclosurePanel className="border-t border-gray-200 p-5">
                          <div className="grid grid-cols-2 gap-4">
                            {call.contact ? (
                              <div className="flex flex-col">
                                <p className="text-sm text-blue-600 font-medium mb-1">Contact</p>
                                <Link
                                  to={`/contacts/${call.contact.uuid}`}
                                  className="text-gray-800 hover:text-blue-600 transition-colors"
                                >
                                  {call.contact.display_name}
                                </Link>
                              </div>
                            ) : (
                              <>
                                {call.task_recipient_slug !== "anyone" && (
                                  <div className="flex flex-col">
                                    <p className="text-sm text-blue-600 font-medium mb-1">Recipient</p>
                                    <p className="text-gray-800">{callService.getRecipientFrontendName()}</p>
                                  </div>
                                )}
                              </>
                            )}
                            <div className="flex flex-col">
                              <p className="text-sm text-blue-600 font-medium mb-1">Language</p>
                              <p className="text-gray-800">{callService.getCall().language_name}</p>
                            </div>
                            {callService.getPhoneNumber() != null && (
                              <div className="flex flex-col">
                                <p className="text-sm text-blue-600 font-medium mb-1">Phone</p>
                                <p className="text-gray-800">{callService.getPhoneNumber()}</p>
                              </div>
                            )}
                            {call.outbound_twilio_phone_number != null && (
                              <div className="flex flex-col">
                                <p className="text-sm text-blue-600 font-medium mb-1">Outbound Phone</p>
                                <Link
                                  to={`/phone-numbers/${call.outbound_twilio_phone_number.uuid}`}
                                  className="text-gray-800 hover:text-blue-600 transition-colors"
                                >
                                  {formatAnyPhoneNumber(
                                    call.outbound_twilio_phone_number.country_code,
                                    call.outbound_twilio_phone_number.phone_number
                                  )}
                                </Link>
                              </div>
                            )}
                            {callService.completed() && (
                              <div className="flex flex-col">
                                <p className="text-sm text-blue-600 font-medium mb-1">Date</p>
                                <p className="text-gray-800">{callService.timestampStarted()}</p>
                              </div>
                            )}
                            {call.dynamic_task != null && (
                              <div className="flex flex-col">
                                <p className="text-sm text-blue-600 font-medium mb-1">Task</p>
                                <p className="text-gray-800">{call.dynamic_task.task_short}</p>
                              </div>
                            )}
                            {call.campaign != null && (
                              <div className="flex flex-col">
                                <p className="text-sm text-blue-600 font-medium mb-1">Campaign</p>
                                <Link
                                  to={`/campaigns/${call.campaign.uuid}`}
                                  className="text-gray-800 hover:text-blue-600 transition-colors"
                                >
                                  {call.campaign.name}
                                </Link>
                              </div>
                            )}
                            {call?.task_template != null && !call.task_template.internal && (
                              <div className="flex flex-col">
                                <p className="text-sm text-blue-600 font-medium mb-1">Task Template</p>
                                <Link
                                  to={`/task-templates/${call.task_template.uuid}`}
                                  className="text-gray-800 hover:text-blue-600 transition-colors"
                                >
                                  {call.task_template.task_name}
                                </Link>
                              </div>
                            )}
                            {userDocuments.length > 0 && (
                              <div className="flex flex-col">
                                <p className="text-sm text-blue-600 font-medium mb-1">
                                  Document{userDocuments.length > 1 ? "s" : ""}
                                </p>
                                {userDocuments.map((doc: any, index: number) => (
                                  <a
                                    href={doc.url}
                                    key={index}
                                    className="text-gray-800 hover:text-blue-600 transition-colors"
                                  >
                                    {doc.name}
                                  </a>
                                ))}
                              </div>
                            )}
                          </div>

                          {/* Scheduled Call Section */}
                          {call.status == "scheduled" && scheduledAt != null && (
                            <div className="mt-5 pt-5 border-t border-gray-200">
                              <div className="flex flex-col gap-4">
                                <div>
                                  <p className="text-sm text-blue-600 font-medium mb-1">Scheduled For</p>
                                  <p className="text-gray-800 font-normal">
                                    {moment(scheduledAt).format("MMM D, YYYY h:mm A")}
                                  </p>
                                </div>
                                <button
                                  onClick={handleCancelScheduledCall}
                                  className="w-min bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded-xl shadow-sm hover:shadow-md transition-all"
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          )}

                          {/* Retry Section */}
                          {call.retry_schedule && call.retry_schedule.retries > 0 && (
                            <div className="mt-5 pt-5 border-t border-gray-200">
                              <div className="flex flex-col gap-4">
                                <div>
                                  {call.id === call.retry_schedule.source_call.id ? (
                                    <>
                                      <p className="text-sm text-blue-600 font-medium mb-1">Retry Status</p>
                                      {!call.retry_schedule.completed ? (
                                        <p className="flex items-center text-gray-800">
                                          Still trying for you... We&apos;ll keep redialing.
                                          <span className="ml-2 animate-pulse text-blue-500">●</span>
                                        </p>
                                      ) : call.retry_schedule.succeeded ? (
                                        <p className="text-green-600 font-medium">
                                          Success! We got through on a later attempt.
                                        </p>
                                      ) : (
                                        <p className="text-red-600 font-medium">
                                          We couldn&apos;t get through after several attempts.
                                        </p>
                                      )}
                                    </>
                                  ) : (
                                    <>
                                      <p className="text-sm text-blue-600 font-medium mb-1">Retry Status</p>
                                      <p className="text-gray-800">Viewing a retry attempt.</p>
                                    </>
                                  )}
                                </div>

                                <div className="flex flex-col gap-2">
                                  {!call.retry_schedule.completed && (
                                    <button
                                      onClick={handleCancelRetries}
                                      className="bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded-xl shadow-sm hover:shadow-md transition-all disabled:bg-red-400 w-fit"
                                      disabled={isCancellingRetries}
                                    >
                                      {isCancellingRetries ? "Cancelling..." : "Cancel Retries"}
                                    </button>
                                  )}

                                  {call.id !== call.retry_schedule.source_call.id && (
                                    <Link to={`/calls/${call.retry_schedule.source_call.id}`}>
                                      <button className="bg-linear-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-medium py-2 px-4 rounded-xl shadow-sm hover:shadow-md transition-all">
                                        View Original Call
                                      </button>
                                    </Link>
                                  )}

                                  {call.retry_schedule.call_retries.length > 0 &&
                                    call.retry_schedule.call_retries[call.retry_schedule.call_retries.length - 1].id !==
                                      call.id &&
                                    call.retry_schedule.succeeded && (
                                      <Link
                                        to={`/calls/${
                                          call.retry_schedule.call_retries[call.retry_schedule.call_retries.length - 1]
                                            .id
                                        }`}
                                      >
                                        <button className="bg-linear-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-medium py-2 px-4 rounded-xl shadow-sm hover:shadow-md transition-all text-sm">
                                          View Successful Retry
                                        </button>
                                      </Link>
                                    )}
                                </div>
                              </div>
                            </div>
                          )}
                        </DisclosurePanel>
                      </div>
                    )}
                  </Disclosure>
                </div>

                {/* Call Results Card - Moved right after Call Details */}
                {callResults != null && (
                  <CallResults key={`results-${call.id}`} call={callResults} recordingUrl={recordingUrl} />
                )}

                {/* Active Call Controls */}
                {!callService.completed() && call.status != "scheduled" && (
                  <div className="bg-white shadow-sm rounded-xl border border-gray-200 overflow-hidden">
                    <div className="flex w-full justify-between items-center p-5">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-purple-100 rounded-lg">
                          <Phone className="w-5 h-5 text-purple-600" />
                        </div>
                        <h3 className="text-base font-medium text-gray-900">Call Controls</h3>
                        <TourTrigger
                          onClick={triggerTour}
                          tooltipContent="Click to replay the interface tour and learn how to use the call controls"
                        />
                      </div>
                    </div>
                    <div className="border-t border-gray-200 p-5">
                      <div className="flex flex-col items-center gap-4">
                        {callService.getFrontendStatus() == "queued" && (
                          <div className="animate-pulse text-gray-500 text-center">Setting up your call now...</div>
                        )}

                        {callService.getFrontendStatus() == "in_progress" && messages.length == 0 && (
                          <div className="animate-pulse text-gray-500 text-center">
                            Dialing. Please wait a few moments...
                          </div>
                        )}

                        <div className="flex flex-wrap gap-4 justify-center w-full">
                          {!playCall ? (
                            <button
                              id="listen-button"
                              onClick={() => setPlayCall(true)}
                              disabled={callService.getFrontendStatus() !== "in_progress" || isPlaying}
                              className="bg-linear-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-medium py-3 px-5 rounded-xl shadow-sm hover:shadow-md transition-all w-[200px] shrink-0 flex items-center justify-between disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <span>LISTEN</span>
                              <Play />
                            </button>
                          ) : (
                            <button
                              id="mute-button"
                              onClick={() => setPlayCall(false)}
                              disabled={callService.getFrontendStatus() !== "in_progress"}
                              className="bg-linear-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-medium py-3 px-5 rounded-xl shadow-sm hover:shadow-md transition-all w-[200px] shrink-0 flex items-center justify-between disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <span>{!isPlaying ? "CONNECTING..." : "MUTE"}</span>
                              <Pause />
                            </button>
                          )}

                          <button
                            id="take-over-button"
                            onClick={() => setShowTakeOverModal(true)}
                            disabled={callService.getFrontendStatus() !== "in_progress"}
                            className="bg-linear-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white font-medium py-3 px-5 rounded-xl shadow-sm hover:shadow-md transition-all w-[200px] flex items-center justify-between disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <span>TAKE OVER</span>
                            <HeadsetHelp />
                          </button>

                          <button
                            id="send-instructions-button"
                            onClick={() => setShowTranscriptModal(true)}
                            disabled={callService.getFrontendStatus() !== "in_progress"}
                            className="lg:hidden bg-linear-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white font-medium py-3 px-5 rounded-xl shadow-sm hover:shadow-md transition-all w-[200px] flex items-center justify-between disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <span>INSTRUCT AI</span>
                            <AlignLeft />
                          </button>

                          <button
                            id="end-button"
                            onClick={handleHangupClick}
                            className="bg-linear-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white font-medium py-3 px-5 rounded-xl shadow-sm hover:shadow-md transition-all w-[200px] flex items-center justify-between"
                          >
                            <span>END CALL</span>
                            <PhoneXmark />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Retry Info Card */}
                {call.retry_schedule && <RetryInfoCard key={`retry-${call.id}`} call={call} onUpdate={setCall} />}

                {/* Mobile Transcript Toggle */}
                <div className="lg:hidden">
                  <div className="bg-white border border-gray-200 shadow-sm rounded-xl">
                    <button
                      onClick={() => setShowTranscriptModal(true)}
                      id="view-transcript-button"
                      className="flex w-full justify-between items-center rounded-xl p-5"
                    >
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-purple-100 rounded-lg">
                          <AlignLeft className="w-5 h-5 text-purple-600" />
                        </div>
                        <p className="text-gray-900 font-medium">View Transcript</p>
                      </div>
                      <NavArrowDown className="h-5 w-5 text-blue-500" />
                    </button>
                  </div>
                  <FullscreenTranscriptOverlay
                    call={call}
                    messages={messages}
                    sendInstructions={sendInstructions}
                    isOpen={showTranscriptModal}
                    onClose={() => setShowTranscriptModal(false)}
                  />
                </div>

                {/* Call Information Card */}
                {callService != null &&
                  taskRecipientSvc != null &&
                  CallInfoCard({
                    call: call,
                    callService: callService,
                    dynamicTaskSvc: dynTaskSvc,
                    recipientSvc: taskRecipientSvc
                  })}

                {/* Call Settings Card */}
                <CallSettingsCard key={`call-settings-${call.id}`} call={call} />
              </div>
            </div>

            {/* Right Column - Transcript - always visible */}
            <div className="hidden lg:flex lg:col-span-7 flex-col bg-white border border-gray-200 shadow-sm rounded-xl overflow-hidden">
              <div className="flex items-center p-5 border-b border-gray-200">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <AlignLeft className="w-5 h-5 text-purple-600" />
                  </div>
                  <h2 className="text-base font-medium text-gray-900">Live Transcript</h2>
                </div>
              </div>
              <div className="flex-1 overflow-hidden">
                <Transcript
                  key={`transcript-desktop-${call.id}`}
                  call={call}
                  messages={messages}
                  otherPartyName={undefined}
                  showSendMessage
                  onSendMessage={sendInstructions}
                />
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
function CallInfoCard({
  call,
  callService,
  dynamicTaskSvc,
  recipientSvc
}: {
  call: any
  callService: CallService
  dynamicTaskSvc: DynamicTaskService
  recipientSvc: RecipientService
}): React.JSX.Element {
  let params = recipientSvc.getParams() || []
  params = params.concat(dynamicTaskSvc.getParams() || [])

  const nameToValue = params.reduce((obj: any, param: any) => {
    let val = callService.getParamValue(param.slug)
    if (val != null) {
      if (typeof val === "boolean") {
        val = val ? "Yes" : "No"
      } else {
        val = val.trim()
      }
    }
    if (val != null && val != "") {
      obj[param.name] = val
    }
    return obj
  }, {})

  // Add prank call specific information
  if (call.task?.slug === "prank_call") {
    const voiceSlug = call.task_parameters?.["voice-slug"]
    const callTypeSlug = call.task_parameters?.["call-type-slug"]

    if (voiceSlug) {
      const voice = call.task.additional_public_metadata.voices?.find((v: any) => v.slug === voiceSlug)
      const callType = voice?.call_types?.find((ct: any) => ct.slug === callTypeSlug)

      nameToValue["Voice"] = voice?.name || ""
      nameToValue["Call Type"] = callType?.name || ""
      nameToValue["Description"] = callType?.description || ""
    }
  }

  if (call.task_template) {
    const taskSlugToName: { [key: string]: string } = {}
    const taskSlugToValue: { [key: string]: string } = {}
    call.task_template.full_task_properties.forEach((param: any) => {
      taskSlugToName[param.slug] = param.name
    })
    call.task_template_parameters.forEach((param: any) => {
      taskSlugToValue[param.slug] = param.value
    })

    Object.keys(taskSlugToValue).forEach((slug) => {
      const name = taskSlugToName[slug]
      const value = taskSlugToValue[slug]
      if (name != null && value != null && value != "") {
        nameToValue[name] = value
      }
    })
  }

  if (call.missing_data_from_previous_call != null) {
    call.missing_data_from_previous_call.forEach((param: any) => {
      let slug = param.slug
      if (slug == null) {
        slug = param.name.toLowerCase().replace(/\W/g, "_")
      }
      nameToValue[param.name] = call.task_missing_data_parameters[slug]
    })
  }

  if (call.prompt) {
    nameToValue["Instructions"] = call.prompt
  }

  const hasInfoToShow = Object.keys(nameToValue).length > 0

  return (
    <div className="bg-white shadow-sm rounded-xl border border-gray-200 overflow-hidden">
      <Disclosure defaultOpen={false}>
        {({ open }) => (
          <div>
            <DisclosureButton
              id="call-info-disclosure-button"
              className="flex w-full justify-between items-center p-5 hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <AlignLeft className="w-5 h-5 text-purple-600" />
                </div>
                <h3 className="text-base font-medium text-gray-900">Call Information</h3>
              </div>
              <NavArrowDown
                className={`${
                  open ? "rotate-180 transform" : ""
                } h-5 w-5 text-blue-500 transition-transform duration-200`}
              />
            </DisclosureButton>

            <DisclosurePanel className="border-t border-gray-200 p-5">
              {hasInfoToShow ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {Object.keys(nameToValue).map((name) => (
                    <div key={name} className="space-y-1">
                      <div className="text-sm font-medium text-blue-600">{name}</div>
                      <div className="text-gray-800 max-h-[100px] overflow-y-auto rounded-md break-words">
                        {nameToValue[name]}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 italic">No additional information available for this call.</p>
              )}
            </DisclosurePanel>
          </div>
        )}
      </Disclosure>
    </div>
  )
}

function CallStatusBadge({ call }: { call: any }): React.JSX.Element | null {
  const callService = new CallService(call)

  if (!callService.completed()) {
    return null
  }

  const getStatusConfig = () => {
    if (call.succeeded) {
      return {
        text: "Success",
        icon: <CheckCircle className="w-4 h-4" />,
        className: "from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700"
      }
    } else if (call.status === "completed") {
      return {
        text: "Incomplete",
        icon: <Clock className="w-4 h-4" />,
        className: "from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700"
      }
    } else if (call.status === "in_progress") {
      return {
        text: "In Progress",
        icon: <Phone className="w-4 h-4" />,
        className: "from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700"
      }
    } else if (call.status === "scheduled") {
      return {
        text: "Scheduled",
        icon: <Clock className="w-4 h-4" />,
        className: "from-sky-500 to-sky-600 hover:from-sky-600 hover:to-sky-700"
      }
    } else {
      return {
        text: "Queued",
        icon: <Clock className="w-4 h-4" />,
        className: "from-gray-500 to-gray-600 hover:from-gray-600 hover:to-gray-700"
      }
    }
  }

  const config = getStatusConfig()

  return (
    <div
      className={`bg-linear-to-r ${config.className} text-white font-medium px-4 py-2 rounded-xl shadow-sm hover:shadow-md transition-all flex items-center gap-2`}
    >
      {config.icon}
      {config.text}
    </div>
  )
}

function TotalTimeButton({ call }: { call: any }): React.JSX.Element {
  const [timeStr, setTimeStr] = useState("00:00")
  const callService = new CallService(call)

  const effect = callService.getFrontendStatus() == "in_progress" ? "animate-pulse" : ""

  useEffect(() => {
    function updateTime() {
      if (call.duration != null) {
        const hours = Math.floor(call.duration / 3600)
        const minutes = Math.floor((call.duration % 3600) / 60).toLocaleString("en-US", {
          minimumIntegerDigits: 2,
          useGrouping: false
        })
        const seconds = Math.floor((call.duration % 3600) % 60).toLocaleString("en-US", {
          minimumIntegerDigits: 2,
          useGrouping: false
        })

        setTimeStr(hours == 0 ? `${minutes}:${seconds}` : `${hours}:${minutes}:${seconds}`)
      } else {
        if (callService.getFrontendStatus() == "in_progress" && call.started_at != null) {
          const startTime = new Date(call.started_at)
          const now = new Date()
          const diff = now.getTime() - startTime.getTime()
          const diffSeconds = Math.max(0, Math.floor(diff / 1000))
          const hours = Math.floor(diffSeconds / 3600)
          const minutes = Math.floor((diffSeconds % 3600) / 60).toLocaleString("en-US", {
            minimumIntegerDigits: 2,
            useGrouping: false
          })
          const seconds = Math.floor((diffSeconds % 3600) % 60).toLocaleString("en-US", {
            minimumIntegerDigits: 2,
            useGrouping: false
          })

          setTimeStr(hours == 0 ? `${minutes}:${seconds}` : `${hours}:${minutes}:${seconds}`)
        } else {
          setTimeStr("00:00")
        }
      }
    }

    // Initial update
    updateTime()

    // Set up interval if call is in progress
    let interval: any = null
    if (callService.getFrontendStatus() == "in_progress" && call.started_at != null) {
      interval = setInterval(updateTime, 1000)
    }

    // Cleanup
    return () => {
      if (interval) {
        clearInterval(interval)
      }
    }
  }, [call, callService])

  return (
    <div
      className={`${effect} bg-linear-to-r from-blue-700 to-indigo-800 hover:from-blue-800 hover:to-indigo-900 text-white font-medium px-4 py-2 rounded-xl shadow-sm hover:shadow-md transition-all flex items-center gap-2`}
    >
      <Clock className="w-4 h-4" />
      <span>{timeStr}</span>
    </div>
  )
}

export function TakeOverCallModal({
  call,
  showModal,
  setShowModal,
  onClose
}: {
  call: Call
  title?: string
  showModal: boolean
  setShowModal: (show: boolean) => void
  onClose?: () => void
}): React.JSX.Element {
  const [selectedCallbackNumber, setSelectedCallbackNumber] = useState("")
  const [verifiedCallbackNumbers, setVerifiedCallbackNumbers] = useState<CallbackPhoneNumber[]>([])
  const [successMessage, setSuccessMessage] = useState("")
  const [errorMessage, setErrorMessage] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const vibratoClient = useContext(VibratoClientContext)

  useEffect(() => {
    if (showModal && vibratoClient) {
      vibratoClient.getCallbackPhoneNumbers({ verified: true }).then((numbers: CallbackPhoneNumber[]) => {
        setVerifiedCallbackNumbers(numbers)
        if (numbers.length > 0) {
          setSelectedCallbackNumber(numbers[0].uuid)
        }
      })
    }
  }, [showModal, vibratoClient])

  const handleTakeOver = async () => {
    if (!selectedCallbackNumber) {
      setErrorMessage("Please select a callback number.")
      return
    }

    setIsLoading(true)
    try {
      const selectedNumber = verifiedCallbackNumbers.find((n) => n.uuid === selectedCallbackNumber)
      if (!selectedNumber) {
        throw new Error("Selected callback number not found.")
      }

      await vibratoClient.takeOverCall(call.id, selectedNumber.country_code, selectedNumber.phone_number)
      setSuccessMessage("Call taken over successfully. You will be connected shortly...")
      setErrorMessage("")
    } catch (e) {
      if (e instanceof ApiError) {
        let errorMsg = "Error: "

        if (typeof e.data === "object") {
          for (const key in e.data) {
            let details = ""

            if (typeof e.data[key] === "object") {
              details = e.data[key].join(", ")
              errorMsg += `${key}: ${details}. `
            } else {
              errorMsg += e.data[key]
            }
          }
        } else {
          errorMsg += JSON.stringify(e.data)
        }

        setSuccessMessage("")
        setErrorMessage(errorMsg)
        return
      }

      setSuccessMessage("")
      setErrorMessage("An error occurred while taking over the call. Please try again.")
    } finally {
      setIsLoading(false)
    }

    await new Promise((resolve) => setTimeout(resolve, 5000))

    setShowModal(false)
    if (onClose) onClose()
  }

  return (
    <Modal
      show={showModal}
      onClose={() => {
        if (onClose) onClose()
        setShowModal(false)
      }}
      size="md"
      className="pt-safe"
      theme={{
        content: {
          inner: "relative rounded-xl bg-white shadow dark:bg-gray-700 overflow-hidden"
        }
      }}
    >
      <div className="p-0 overflow-hidden">
        {/* Header with gradient background */}
        <div className="w-full p-6 bg-linear-to-r from-blue-600 to-purple-600">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-white/20 rounded-xl">
              <Phone className="w-6 h-6 text-white" />
            </div>
            <h3 className="text-xl font-bold text-white">Take Over Call</h3>
          </div>
        </div>

        {/* Body content */}
        <div className="p-6 space-y-6">
          {/* Explanation card */}
          <div className="bg-blue-50 border border-blue-100 rounded-xl p-4">
            <p className="text-blue-800 text-sm">
              Taking over the call will connect you directly to the recipient. The AI assistant will be disconnected.
            </p>
          </div>

          {/* Warning card */}
          <div className="bg-amber-50 border border-amber-100 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <div className="p-1.5 bg-amber-100 rounded-lg">
                <Shield className="w-5 h-5 text-amber-600" />
              </div>
              <p className="text-amber-800 text-sm">
                <strong>Important:</strong> This action will immediately drop Vibrato&apos;s AI from the call and
                connect you directly.
              </p>
            </div>
          </div>

          {/* Callback number selection */}
          <div className="space-y-3">
            <label htmlFor="callbackNumber" className="block text-sm font-medium text-gray-700">
              Select your callback number
            </label>

            {verifiedCallbackNumbers.length > 0 ? (
              <div className="relative">
                <select
                  id="callbackNumber"
                  value={selectedCallbackNumber}
                  onChange={(e) => setSelectedCallbackNumber(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base bg-white appearance-none"
                >
                  {verifiedCallbackNumbers.map((number) => (
                    <option key={number.uuid} value={number.uuid}>
                      {formatAnyPhoneNumber(number.country_code, number.phone_number)}
                    </option>
                  ))}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-gray-500">
                  <NavArrowDown className="w-5 h-5" />
                </div>
              </div>
            ) : (
              <div className="bg-yellow-50 border border-yellow-100 rounded-xl p-4 flex items-start gap-3">
                <div className="p-1.5 bg-yellow-100 rounded-lg">
                  <Phone className="w-5 h-5 text-yellow-600" />
                </div>
                <p className="text-yellow-800 text-sm">
                  You don&apos;t have any verified callback numbers. Please add and verify a number to continue.
                </p>
              </div>
            )}

            <Link
              to="/callback-numbers"
              className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 text-sm font-medium"
            >
              <Phone className="w-4 h-4" />
              Manage callback numbers
            </Link>
          </div>

          {/* Status messages */}
          {errorMessage && (
            <div className="bg-red-50 border border-red-100 rounded-xl p-4 text-red-800 text-sm">{errorMessage}</div>
          )}

          {successMessage && (
            <div className="bg-green-50 border border-green-100 rounded-xl p-4 flex items-start gap-3">
              <div className="p-1.5 bg-green-100 rounded-lg">
                <Phone className="w-5 h-5 text-green-600" />
              </div>
              <p className="text-green-800 text-sm">{successMessage}</p>
            </div>
          )}

          {/* Action buttons - only show if no success message */}
          {!successMessage && (
            <div className="flex flex-col gap-4 pt-4">
              <button
                onClick={() => setShowModal(false)}
                className="flex-1 px-6 py-3 border border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors font-medium"
              >
                Cancel
              </button>

              <button
                onClick={handleTakeOver}
                disabled={verifiedCallbackNumbers.length === 0 || isLoading}
                className={`flex-1 px-6 py-3 rounded-xl font-medium flex items-center justify-center gap-2 ${
                  verifiedCallbackNumbers.length === 0
                    ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                    : "bg-linear-to-r from-blue-600 to-purple-600 text-white hover:shadow-lg transition-all"
                }`}
              >
                {isLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                    <span>Connecting...</span>
                  </>
                ) : (
                  <>
                    <Phone className="w-5 h-5" />
                    Take Over Call
                  </>
                )}
              </button>
            </div>
          )}

          {/* Close button - only show after successful takeover */}
          {successMessage && (
            <div className="flex justify-center pt-4">
              <button
                onClick={() => setShowModal(false)}
                className="px-6 py-3 bg-linear-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:shadow-lg transition-all font-medium flex items-center justify-center gap-2"
              >
                <CheckCircle className="w-5 h-5" />
                Close
              </button>
            </div>
          )}
        </div>
      </div>
    </Modal>
  )
}
function FullscreenTranscriptOverlay({
  call,
  messages,
  sendInstructions,
  isOpen,
  onClose
}: {
  call: any
  messages: any[]
  sendInstructions: (message: string) => void
  isOpen: boolean
  onClose: () => void
}) {
  const userSession = useContext(UserContext)
  const [searchParams] = useSearchParams()

  // Helper function to determine if the tour should be shown for transcript overlay
  const shouldShowTranscriptTour = useCallback((userSession: any, forceShow: boolean = false): boolean => {
    if (!userSession) return false

    // If forced via URL param or manual trigger, always show
    if (forceShow) return true

    // Check if tour has been shown before in localStorage
    const tourShownKey = `transcript_tour_shown_${userSession.id}`
    const tourShown = localStorage.getItem(tourShownKey)
    if (tourShown === "true") {
      return false
    }

    // Check if user was created more than a week ago
    if (userSession.created_at) {
      const userCreatedAt = new Date(userSession.created_at)
      const oneWeekAgo = new Date()
      oneWeekAgo.setDate(oneWeekAgo.getDate() - 7)

      if (userCreatedAt < oneWeekAgo) {
        return false
      }
    }

    return true
  }, [])

  // Helper function to mark tour as shown for transcript overlay
  const markTranscriptTourAsShown = useCallback((userSession: any): void => {
    if (userSession) {
      const tourShownKey = `transcript_tour_shown_${userSession.id}`
      localStorage.setItem(tourShownKey, "true")
    }
  }, [])

  // Function to manually trigger the transcript tour
  const triggerTranscriptTour = useCallback(() => {
    console.log("Manually triggering transcript tour")
    // TODO: Replace with actual tour ID when provided
    usertour.start("cmcm7xj9102ufa3msd3k06306")
    markTranscriptTourAsShown(userSession)
  }, [userSession, markTranscriptTourAsShown])

  // Show tour when overlay opens if conditions are met
  useEffect(() => {
    if (isOpen && userSession) {
      setTimeout(() => {
        const forceTour = searchParams.get("transcript_tour") === "true"
        if (shouldShowTranscriptTour(userSession, forceTour)) {
          console.log("Starting transcript tour")
          // TODO: Replace with actual tour ID when provided
          usertour.start("cmcm7xj9102ufa3msd3k06306")
          markTranscriptTourAsShown(userSession)
        } else {
          console.log("Transcript tour skipped - already shown or user too old")
        }
      }, 1000)
    }
  }, [isOpen, userSession, searchParams, shouldShowTranscriptTour, markTranscriptTourAsShown])

  if (!isOpen) return null

  return (
    <div
      className="fixed pt-safe inset-0 bg-white z-50 flex flex-col"
      style={{
        paddingBottom: "var(--keyboard-height, 0px)"
      }}
    >
      {/* Header with back button */}
      <div className="flex items-center gap-4 px-4 py-3 bg-linear-to-r from-purple-600 to-indigo-600 text-white shadow-md">
        <button
          onClick={onClose}
          className="p-2 rounded-full hover:bg-white/10 transition-colors flex items-center justify-center"
          aria-label="Close transcript"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="h-5 w-5"
          >
            <path d="M15 18l-6-6 6-6" />
          </svg>
        </button>
        <h1 className="text-lg font-semibold">Call Transcript</h1>

        {/* Tour trigger button */}
        <div className="ml-auto">
          <TourTrigger
            onClick={triggerTranscriptTour}
            tooltipContent="Click to replay the transcript interface tour and learn how to use the transcript features"
            className="text-white/80 hover:text-white"
          />
        </div>
      </div>

      {/* Transcript content with proper scrolling */}
      <div className="flex-1 overflow-auto">
        <Transcript
          key={`transcript-fullscreen-${call.id}`}
          call={call}
          messages={messages}
          otherPartyName={undefined}
          showSendMessage
          onSendMessage={sendInstructions}
        />
      </div>
    </div>
  )
}

function ShareCallModal({
  call,
  showModal,
  setShowModal,
  onClose
}: {
  call: Call
  showModal: boolean
  setShowModal: (show: boolean) => void
  onClose?: () => void
}): React.JSX.Element {
  const [shareUrl, setShareUrl] = useState<string>("")
  const [isShared, setIsShared] = useState(call.is_public)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string>("")
  const vibratoClient = useContext(VibratoClientContext)

  const handleShare = async () => {
    if (!call) return
    setIsLoading(true)
    setError("")

    try {
      const shareDetails = await vibratoClient.shareCall(call.id)
      setShareUrl(shareDetails.share_url)
      setIsShared(true)
      call.is_public = true
      call.public_uuid = shareDetails.public_uuid
    } catch (error) {
      console.error("Error sharing call:", error)
      setError("Failed to share call. Please try again.")
    } finally {
      setIsLoading(false)
    }
  }

  const handleUnshare = async () => {
    if (!call) return
    setIsLoading(true)
    setError("")

    try {
      await vibratoClient.unshareCall(call.id)
      setShareUrl("")
      setIsShared(false)
      call.is_public = false
      call.public_uuid = null
    } catch (error) {
      console.error("Error unsharing call:", error)
      setError("Failed to unshare call. Please try again.")
    } finally {
      setIsLoading(false)
    }
  }

  const handleCopyUrl = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl)
    } catch (error) {
      console.error("Error copying URL:", error)
    }
  }

  useEffect(() => {
    if (showModal) {
      if (call.is_public) {
        setShareUrl(`${window.location.origin}/public-calls/${call.public_uuid}`)
      } else {
        setShareUrl("")
      }
    }
  }, [showModal])

  return (
    <Modal
      show={showModal}
      onClose={() => {
        if (onClose) onClose()
        setShowModal(false)
        setError("")
      }}
      size="2xl"
      className="pt-safe"
    >
      <ModalHeader className="border-b border-gray-200">
        <span className="text-blue-600">Share Call 🔗</span>
      </ModalHeader>
      <ModalBody>
        <div className="flex flex-col gap-4">
          {error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center gap-2">
                <svg className="h-5 w-5 text-red-500" viewBox="0 0 20 20" fill="currentColor">
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
                <span className="text-sm font-medium text-red-800">{error}</span>
              </div>
            </div>
          )}
          {isShared ? (
            <>
              <p className="font-roboto">Share this link to allow others to view this call:</p>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={shareUrl}
                  readOnly
                  className="grow py-2 px-3 block w-full border-gray-200 rounded-md text-base focus:border-blue-500 focus:ring-blue-500 dark:bg-slate-900 dark:border-gray-700 dark:text-gray-400"
                />
                <button
                  onClick={handleCopyUrl}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  Copy
                </button>
              </div>
            </>
          ) : (
            <p className="font-roboto">Share this call to create a public link that anyone can use to view it.</p>
          )}
        </div>
      </ModalBody>
      <ModalFooter>
        <div className="flex justify-end gap-2">
          <button
            className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500"
            onClick={() => setShowModal(false)}
          >
            Close
          </button>
          {isShared ? (
            <button
              className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
              onClick={handleUnshare}
              disabled={isLoading}
            >
              {isLoading ? "Unsharing..." : "Unshare"}
            </button>
          ) : (
            <button
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
              onClick={handleShare}
              disabled={isLoading}
            >
              {isLoading ? "Sharing..." : "Share"}
            </button>
          )}
        </div>
      </ModalFooter>
    </Modal>
  )
}
